import _ from 'lodash';
import { useApi, useWorkspace } from '~/contexts';
import { useField, useIsMounted } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, weights } from '~/styles';
import AutoComplete from './AutoComplete';
import Dropdown from './Dropdown';
import FieldControl from './FieldControl';
import { accountingProviders } from '~/lookups';

const Group = styled(Dropdown.Text)`
  font-weight: ${weights.bold};
  font-size: 1rem;
  color: ${colors.grey100};
`;

const AccountingIntegrationSelect = React.forwardRef(({ value, onChange, ...props }, ref) => {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [defaultOptions, setDefaultOptions] = useState([]);

  const handleSearch = useCallback(
    async (q) => {
      const { data } = await api.www.workspaces(workspace.id).integrations().accounting.options({
        q,
      });

      const grouped = _.groupBy(data, 'provider');
      const groups = Object.entries(grouped)
        .map(([provider, integrations]) => ({
          provider,
          integrations,
          label: accountingProviders[provider]?.name || provider,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      const options = [];
      for (const group of groups) {
        options.push({ type: 'group', key: group.provider, label: group.label, disabled: true });
        group.integrations.forEach((integration) => {
          options.push({ type: integration.provider, integration, disabled: false });
        });
      }

      return options;
    },
    [workspace.id, api],
  );

  const refreshDefaultOptions = useCallback(async () => {
    const data = await handleSearch('');
    if (!isMounted.current) return;
    setDefaultOptions(data);
  }, [handleSearch, isMounted]);

  useEffect(() => {
    refreshDefaultOptions();
  }, [refreshDefaultOptions]);

  const handleChange = (event) => {
    const integration = event.target.value?.integration || event.target.value;
    const changeArgument = {
      target: {
        name: event.target.name,
        value: integration
          ? {
              accountingProvider: integration.provider,
              accountingConnectionId: integration.id,
              name: integration.name,
            }
          : null,
      },
    };
    onChange(changeArgument);
  };

  return (
    <AutoComplete
      ref={ref}
      onSearch={handleSearch}
      value={value}
      displayText={value ? value.name : ''}
      defaultOptions={defaultOptions}
      onChange={handleChange}
      renderValue={(option) => option.integration.name}
      clearable={true}
      placeholder="Accounting Integration"
      {...props}>
      {({ options }) => {
        let index = 0;

        return (
          <>
            {options.length > 0 ? (
              options.map((option) => {
                switch (option.type) {
                  case 'group':
                    return (
                      <Group key={option.key} style={{ marginTop: '0.5rem' }}>
                        <strong>{option.label}</strong>
                      </Group>
                    );
                  case 'xero':
                  case 'qbo':
                  case 'sage':
                    return (
                      <AutoComplete.Option
                        padding="0.25rem 0 0.25rem 2rem"
                        key={option.integration.id}
                        value={option}
                        index={index++}
                        tooltip={option.integration.name}>
                        {option.integration.name}
                      </AutoComplete.Option>
                    );
                  default:
                    return null;
                }
              })
            ) : (
              <Dropdown.Text>None</Dropdown.Text>
            )}
          </>
        );
      }}
    </AutoComplete>
  );
});

const FieldAccountingIntegrationSelect = React.forwardRef((props, ref) => {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;

  return (
    <FieldControl error={error}>
      <AccountingIntegrationSelect ref={ref} {...field} {...props} />
    </FieldControl>
  );
});

export default AccountingIntegrationSelect;
export { FieldAccountingIntegrationSelect };
